import { createFileRoute, notFound } from "@tanstack/react-router";
import { findModelo, MARCAS, MODELOS, MIN_LISTINGS_TO_INDEX, type SeoEntry } from "@/lib/seo-config";
import { fetchSeoListings } from "@/lib/seo-data";
import { buildFaqs, buildIntro } from "@/lib/seo-copy";
import { breadcrumbJsonLd, faqJsonLd, itemListJsonLd, SITE_URL } from "@/lib/seo-jsonld";
import { lastUpdatedAgo } from "@/lib/seo-helpers";
import { SeoPageLayout } from "@/components/seo/SeoPageLayout";
import { SEO_LOCATIONS } from "@/lib/locations";

function entryFromSlug(slug: string): SeoEntry {
  const words = slug.split("-");
  const displayName = words.map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(" ");
  return {
    slug,
    displayName,
    brand: words[0].charAt(0).toUpperCase() + words[0].slice(1),
    match: [words.join(" ")],
  };
}

export const Route = createFileRoute("/blindados/modelo/$slug")({
  loader: async ({ params }) => {
    const entry = findModelo(params.slug) ?? entryFromSlug(params.slug);
    const { listings, stats } = await fetchSeoListings(entry);
    if (stats.total < MIN_LISTINGS_TO_INDEX) throw notFound();
    const intro = buildIntro(entry, stats);
    const faqs = buildFaqs(entry, stats);
    const updatedAgo = lastUpdatedAgo(stats.lastUpdatedAt);
    return { entry, listings, stats, intro, faqs, updatedAgo };
  },
  head: ({ loaderData, params }) => {
    if (!loaderData) return { meta: [{ title: "Página não encontrada" }] };
    const { entry, listings, stats, faqs } = loaderData;
    const url = `${SITE_URL}/blindados/modelo/${params.slug}/`;
    const title = `${entry.displayName} Blindadas — ${stats.total} ofertas${stats.priceMin != null ? ` a partir de R$ ${stats.priceMin.toLocaleString("pt-BR")}` : ""} | Busca Blindados`;
    const description =
      `Veja ${stats.total} ${entry.displayName} blindadas de revendas e marketplaces.` +
      (stats.priceMin != null && stats.priceMax != null
        ? ` Preços de R$ ${stats.priceMin.toLocaleString("pt-BR")} a R$ ${stats.priceMax.toLocaleString("pt-BR")}.`
        : "") +
      " Atualizado hoje.";
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { name: "robots", content: "index,follow" },
        { property: "og:type", content: "website" },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:url", content: url },
        { property: "og:image", content: "https://www.buscablindados.com/og-image.png" },
        { property: "og:image:width", content: "1200" },
        { property: "og:image:height", content: "630" },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: title },
        { name: "twitter:description", content: description },
        { name: "twitter:image", content: "https://www.buscablindados.com/og-image.png" },
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify(
            itemListJsonLd(`${entry.displayName} blindadas`, url, listings, {
              priceMin: stats.priceMin,
              priceMax: stats.priceMax,
              total: stats.total,
              brand: entry.brand,
            }),
          ),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify(faqJsonLd(faqs)),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify(
            breadcrumbJsonLd([
              { name: "Início", url: SITE_URL },
              { name: "Blindados", url: `${SITE_URL}/blindados` },
              { name: entry.displayName, url },
            ]),
          ),
        },
      ],
    };
  },
  notFoundComponent: () => (
    <div className="flex min-h-screen items-center justify-center px-6 text-center">
      <div>
        <h1 className="font-display text-3xl">Sem inventário no momento</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Essa página ainda não tem ofertas suficientes. Volte em breve ou crie um alerta.
        </p>
        <a href="/" className="mt-6 inline-block text-primary underline">
          Início
        </a>
      </div>
    </div>
  ),
  errorComponent: ({ error }) => (
    <div className="flex min-h-screen items-center justify-center px-6 text-center">
      <p className="text-sm text-muted-foreground">{error.message}</p>
    </div>
  ),
  component: ModeloPage,
});

function ModeloPage() {
  const { entry, listings, stats, intro, faqs, updatedAgo } = Route.useLoaderData();
  // Relacionados: 4 outros modelos da mesma marca + 3 marcas
  const sameBrand = MODELOS.filter((m) => m.brand === entry.brand && m.slug !== entry.slug).slice(
    0,
    4,
  );
  const brands = MARCAS.slice(0, 3);
  const related = [
    ...sameBrand.map((m) => ({
      slug: m.slug,
      displayName: m.displayName,
      kind: "modelo" as const,
    })),
    ...brands.map((b) => ({ slug: b.slug, displayName: b.displayName, kind: "marca" as const })),
  ];
  // Only link to UFs we expect to render a non-thin SEO page (≥ MIN_LISTINGS_TO_INDEX).
  const locationLinks = SEO_LOCATIONS.map((l) => ({ slug: l.slug, name: l.display_name }));

  return (
    <SeoPageLayout
      data={{ entry, listings, stats }}
      kind="modelo"
      related={related}
      faqs={faqs}
      intro={intro}
      updatedAgo={updatedAgo}
      locationLinks={locationLinks}
    />
  );
}
